import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useCallback, useMemo } from 'react';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import { htmlFirstLine } from 'src/app/workflows/htmlFirstLine';
import { RFI_STATUS_COLORS } from 'src/app/rfis/rfiStatusColors';
import { useNavigate } from 'react-router-dom';
import { RfiOverviewDto } from 'src/api/queries/rfis/useRfisOverview';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface IRfisOverviewTableProps {
  rfis: RfiOverviewDto[];
  loading?: boolean;
}

export const RfisOverviewTable: React.FC<IRfisOverviewTableProps> = ({
  rfis,
  loading = true,
}) => {
  const navigate = useNavigate();

  const handleViewRfi = useCallback(
    (row: RfiOverviewDto) => {
      navigate(`/jobs/${row.jobId}/rfis/${row.id}`);
    },
    [navigate],
  );

  const columns = useMemo<MRT_ColumnDef<RfiOverviewDto>[]>(
    () => [
      {
        id: 'job',
        accessorFn: (row) => `${row.jobNumber ?? ''} ${row.jobName ?? ''}`,
        header: 'Job',
        size: 220,
        Cell: ({ row }) => (
          <Stack gap={0}>
            <Text c="black" fw={500}>
              {row.original.jobNumber ?? '--'}
            </Text>
            <Text c="black" size="sm">
              {row.original.jobName ?? '--'}
            </Text>
          </Stack>
        ),
      },
      {
        accessorKey: 'rfiNumber',
        header: 'RFI #',
        size: 130,
      },
      workflowStatusColumn<RfiOverviewDto>({
        size: 180,
        colorFallback: (row) => RFI_STATUS_COLORS[row.status ?? ''],
      }),
      {
        id: 'description',
        header: 'Description',
        size: 260,
        accessorFn: (row) => htmlFirstLine(row.question),
      },
      {
        id: 'daysOpen',
        header: 'Days Open',
        size: 120,
        accessorFn: (row) => row.daysOpen ?? undefined,
        Cell: ({ row }) => row.original.daysOpen ?? '',
      },
      {
        id: 'contractor',
        header: 'Contractor',
        size: 200,
        accessorFn: (row) => row.contractorName ?? '',
      },
      {
        id: 'assignee',
        header: 'Assignee',
        size: 180,
        accessorFn: (row) => row.assigneeName ?? '',
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'rfis-overview-table',
    columns: columns,
    defaultSorting: [{ id: 'job', desc: false }],
  });

  const toRow = (item: RfiOverviewDto) => ({
    ['Job Number']: item.jobNumber ?? '',
    ['Job Name']: item.jobName ?? '',
    ['RFI #']: item.rfiNumber ?? '',
    ['Status']: item.status ?? '',
    ['Description']: htmlFirstLine(item.question),
    ['Days Open']: item.daysOpen ?? 0,
    ['Contractor']: item.contractorName ?? '',
    ['Assignee']: item.assigneeName ?? '',
  });

  const tableExports = useTableExports<RfiOverviewDto>('rfis', (data) =>
    data.map(toRow),
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() => handleViewRfi(row.original)}
            label="View RFI"
          />
        </Group>
      ),
      enableClickToCopy: false,
      manualFiltering: false,
      manualSorting: false,
      manualPagination: false,
      columns: columns,
      data: rfis,
    },
    getRowId: (row) => row.id,
    totalCount: rfis.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return table;
};
